/**
 * Phase 4 Task 12 — supervisor AgentGrid.
 *
 * Renders the live grid of agents fed from `useSupervisorStore`. Each
 * tile shows the agent's status (color-coded via PrimeReact Tag),
 * cumulative call count, and live talk-time (when IN_CALL). Force-pause
 * and force-logout buttons emit through the parent's callbacks (the
 * dashboard wires them to `supervisor:force_pause`/`force_logout`
 * socket events that publish via `SupervisorActionEventSchema`).
 */
import type { JSX } from 'react'
import { Tag } from 'primereact/tag'
import { Button } from 'primereact/button'
import type { AgentStatus, SupervisorAgentSummary } from '@kc3/types'

type Severity = 'success' | 'warning' | 'danger' | 'info' | 'secondary'

const STATUS_SEVERITY: Record<AgentStatus, Severity> = {
  IDLE: 'success',
  AVAILABLE_ON_DEMAND: 'success',
  IN_CALL: 'info',
  RINGING: 'warning',
  PAUSED: 'warning',
  ON_BREAK: 'secondary',
  ACW: 'warning',
  OFFLINE: 'danger',
  // Reservation FSM states; map is exhaustive over AgentStatus.
  REGISTERED: 'secondary',
  RESERVING: 'warning',
  RESERVED: 'warning',
  INVITING: 'warning',
  ANSWERED: 'info',
}

interface Props {
  agents: SupervisorAgentSummary[]
  onForcePause: (agentId: string) => void
  onForceLogout: (agentId: string) => void
}

function formatDuration(sec: number): string {
  const m = Math.floor(sec / 60)
  const s = String(sec % 60).padStart(2, '0')
  return `${m}:${s}`
}

export function AgentGrid({
  agents,
  onForcePause,
  onForceLogout,
}: Props): JSX.Element {
  if (agents.length === 0) {
    return (
      <div className="text-sm text-gray-400 p-4" data-testid="agent-grid-empty">
        No agents online.
      </div>
    )
  }
  return (
    <div
      className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3"
      data-testid="agent-grid"
    >
      {agents.map((agent) => (
        <div
          key={agent.id}
          className="border rounded p-3 flex flex-col gap-2 bg-white shadow-sm"
          data-testid={`agent-tile-${agent.id}`}
        >
          <div className="font-semibold text-sm truncate">{agent.name}</div>
          <Tag
            value={agent.status}
            severity={STATUS_SEVERITY[agent.status] ?? 'secondary'}
          />
          <div className="text-xs text-gray-500">
            Calls: {agent.callsHandled}
          </div>
          {agent.status === 'IN_CALL' && (
            <div className="text-xs text-blue-600">
              {formatDuration(agent.callDurationSec)}
            </div>
          )}
          <div className="flex gap-1 mt-auto">
            <Button
              icon="pi pi-pause"
              size="small"
              severity="warning"
              text
              aria-label={`Force pause ${agent.name}`}
              onClick={() => onForcePause(agent.id)}
              tooltip="Force pause"
            />
            <Button
              icon="pi pi-sign-out"
              size="small"
              severity="danger"
              text
              aria-label={`Force logout ${agent.name}`}
              onClick={() => onForceLogout(agent.id)}
              tooltip="Force logout"
            />
          </div>
        </div>
      ))}
    </div>
  )
}
